import {AGE_EXTRACT, NET_VOLUME} from 'modules/product-card/constants/characteristics';
import {plural} from 'core/helpers';
import {IProductSection, IProperty, IPropertyValue} from 'modules/products/types';
import {useCallback, useMemo} from 'react';
import {getProductCanonSection, getProductSpecialCategory} from 'modules/product-card/helpers';
import {BRANDED_CATEGORIES} from 'modules/product-card/helpers/characteristics';
import {PRODUCT_PLACEHOLDER} from 'core/constants';
import {SECTION_CODE} from 'modules/product-card/constants';
import {MANUFACTURER, GRAPE_CONTENT} from 'modules/product-card/constants/characteristics';
import {TProperties, TProperty as TSnippetProperty} from 'shared/types/productSnippet.types';
import {
    FILTER_CODES,
    PROPERTY_CONFIG,
} from 'modules/product-snippet/components/ProductSnippetContainer/ProductSnippetContainer.constants';
import {getTopSection} from 'modules/product-snippet/components/ProductSnippetContainer/helpers';

type TPropertyCode = keyof typeof FILTER_CODES;

const getFilterCode = (code: string): string | undefined => {
    return FILTER_CODES[code as TPropertyCode];
};

type TFormatPropertyValueParams = {
    value: IProperty['value'];
    code: IProperty['code'];
    isListType?: boolean;
    isLast?: boolean;
};

const formatPropertyValue = (params: TFormatPropertyValueParams): TSnippetProperty['formattedValue'] => {
    const {value, code, isListType, isLast} = params;

    let content: string;
    let suffix = '';

    if ('object' === typeof value && value && 'value' in value) {
        content = value.value;
    } else {
        content = String(value);
    }

    if (code === AGE_EXTRACT.KEY) {
        content = `${content} ${plural(Number(content), ['год', 'года', 'лет'])}`;
    }

    if (code === NET_VOLUME.KEY) {
        suffix = '\u00A0л';
    } else if (!isListType && !isLast) {
        suffix = ', ';
    }

    return {
        content,
        suffix,
        code,
    };
};

const filterProperty = (property: IProperty): boolean => {
    if (property.code === NET_VOLUME.KEY) {
        return 0 !== property.value && '0' !== property.value;
    }
    if (property.code === GRAPE_CONTENT.KEY && Array.isArray(property.value)) {
        return 0 < property.value.length;
    }
    return true;
};

const buildBaseUrl = (params: {url: string; canonSection?: IProductSection; topSection?: string}): string => {
    const {url, canonSection, topSection} = params;
    const isKsnSection = topSection === SECTION_CODE.KSN;
    const hasDifferentCanonSection = canonSection?.code && canonSection.code !== topSection;

    if (isKsnSection && hasDifferentCanonSection && canonSection?.code) {
        return `${url}/${canonSection.code}`;
    }
    return url;
};

export interface IUsePropertiesParams {
    properties: IProperty[];
    isListType?: boolean;
    sections: IProductSection[];
    onCatalogClick: (url: string) => void;
}

export const useProductProperties = (params: IUsePropertiesParams): TProperties => {
    const {properties, isListType = false, sections = [], onCatalogClick} = params;

    const topSection = useMemo(() => getTopSection(sections), [sections]);

    const canonSection = useMemo(() => getProductCanonSection(sections), [sections]);
    const url = `/catalog/${topSection}`;

    // определяем, является ли продукт брендированным
    const isProductBranded = useMemo(() => {
        const productCategory = getProductSpecialCategory({
            ...PRODUCT_PLACEHOLDER,
            properties: properties ?? [],
            topSection: topSection ?? '',
            sections: sections ?? [],
        });
        return BRANDED_CATEGORIES.includes(productCategory);
    }, [properties, sections, topSection]);

    // обрабатываем свойства и создаем URL для фильтров
    const processedProperties = useMemo(() => {
        if (!properties) {
            return [];
        }

        return properties
            .filter((property) => 'bool' !== property.type && 'int' !== property.type)
            .map((property) => {
                const filterCode = getFilterCode(property.code);
                if (!filterCode) {
                    return null;
                }

                const baseUrl = buildBaseUrl({url, canonSection, topSection});
                let filterUrl: string | string[] | undefined = baseUrl;

                if ('object' === property.type) {
                    const slug = (property.value as IPropertyValue)?.slug;
                    filterUrl += slug ? `/filter/${filterCode}-${slug}` : '';
                } else if ('array<object>' === property.type && Array.isArray(property.value)) {
                    filterUrl = property.value.map((item) => `${baseUrl}/filter/${filterCode}-${item.slug}`);
                } else {
                    const formattedValue =
                        property.code === NET_VOLUME.KEY ? String(property.value).replace('.', '_') : property.value;
                    filterUrl += `/filter/${filterCode}-${formattedValue}`;
                }

                const title = property.code === MANUFACTURER.KEY && isProductBranded ? 'Бренд' : property.title;

                return {
                    ...property,
                    title,
                    filterUrl,
                };
            })
            .filter(Boolean);
    }, [properties, url, canonSection, topSection, isProductBranded]);

    // сортируем свойства
    const sortedProperties = useMemo(() => {
        const config = isListType ? PROPERTY_CONFIG.list : PROPERTY_CONFIG.default;
        const maxIndex = Math.max(...Object.values(config));
        const sorted = new Array(maxIndex + 1).fill(null);

        processedProperties.forEach((property) => {
            if (property) {
                const index = config[property.code as keyof typeof config];
                if (index !== undefined) {
                    sorted[index] = property;
                }
            }
        });

        return sorted.filter(Boolean).filter(filterProperty);
    }, [isListType, processedProperties]);

    // подготавливаем свойства для рендера
    const preparedProperties = useMemo((): TSnippetProperty[] => {
        return sortedProperties.map((property, index) => {
            const isLast = index === sortedProperties.length - 1;
            const formattedValue = formatPropertyValue({
                value: property.value,
                code: property.code,
                isListType,
                isLast,
            });

            return {
                code: property.code,
                type: property.theme,
                formattedValue,
                filterUrl: property.filterUrl ?? null,
                isLast: isLast || null,
            };
        });
    }, [sortedProperties, isListType]);

    const handlePropertyClick = useCallback(
        (filterUrl: string) => {
            onCatalogClick(filterUrl);
        },
        [onCatalogClick],
    );

    const propertyList = isListType
        ? preparedProperties
        : preparedProperties.filter((p) => Object.keys(PROPERTY_CONFIG.default).includes(p.code));

    const asString = useMemo(
        () =>
            propertyList
                .map((property) => `${property.formattedValue.content}${property.formattedValue.suffix}`)
                .join(''),
        [propertyList],
    );

    return {
        list: propertyList,
        asString,
        onPropertyClick: handlePropertyClick,
    };
};
